import { useState, useEffect } from "react";
import { Gamepad2 } from "lucide-react";
import axios, { getApiUrl } from "@/lib/api";

// Platform icons - image URLs for platform logos
// Xbox uses white logo on green badge background for visibility
const PLATFORM_ICONS = {
  xbox: (
    <img 
      src="/platform-logos/xbox-white.png" 
      alt="Xbox" 
      className="w-full h-full object-contain"
    />
  ),
  playstation: (
    <img 
      src="/platform-logos/playstation.png" 
      alt="PlayStation" 
      className="w-full h-full object-contain"
    />
  ),
  steam: (
    <img 
      src="/platform-logos/steam.ico" 
      alt="Steam" 
      className="w-full h-full object-contain"
    />
  ),
  epic: (
    <img 
      src="/platform-logos/epic-white.png" 
      alt="Epic Games" 
      className="w-full h-full object-contain"
    />
  ),
  "card & board games": (
    <img
      src="/platform-logos/card-board.png"
      alt="Card & Board Games"
      className="w-full h-full object-contain"
    />
  ),
  "card-board-games": (
    <img
      src="/platform-logos/card-board.png"
      alt="Card & Board Games"
      className="w-full h-full object-contain"
    />
  ),
  "card-&-board-games": (
    <img
      src="/platform-logos/card-board.png"
      alt="Card & Board Games"
      className="w-full h-full object-contain"
    />
  ),
  nintendo: (
    <img
      src="/platform-logos/nintendo-red.png"
      alt="Nintendo"
      className="w-full h-full object-contain"
    />
  ),
  "google-play": (
    <img
      src="/platform-logos/google-play.png"
      alt="Google Play"
      className="w-full h-full object-contain"
    />
  ),
  "google play": (
    <img
      src="/platform-logos/google-play.png"
      alt="Google Play"
      className="w-full h-full object-contain"
    />
  ),
  gog: (
    <img
      src="/platform-logos/gog.png"
      alt="GOG"
      className="w-full h-full object-contain"
    />
  ),
};

// Green Xbox logo for use on non-badge contexts (like Browse page tiles)
export const XBOX_LOGO_GREEN = "/platform-logos/xbox-green.png";

const DEFAULT_PLATFORM_CONFIG = {
  xbox: { label: "Xbox", color: "#107C10", hasIcon: true },
  playstation: { label: "PlayStation", color: "#003087", hasIcon: true },
  steam: { label: "Steam", color: "#1B2838", hasIcon: true },
  epic: { label: "Epic", color: "#2F2F2F", hasIcon: true },
  nintendo: { label: "Nintendo", color: "#FFFFFF", hasIcon: true },  // White badge so the red Nintendo logo stays visible
  "google-play": { label: "Google Play", color: "#FFFFFF", hasIcon: true },  // White badge so the multi-color Google Play logo stays vibrant
  "google play": { label: "Google Play", color: "#FFFFFF", hasIcon: true },
  pc: { label: "PC", color: "#0078D4" },
  gog: { label: "GOG", color: "#FFFFFF", hasIcon: true },  // White badge so the purple GOG app-icon logo stays vibrant
  "card & board games": { label: "Card & Board Games", color: "#7B2D26", hasIcon: true },
  "card-board-games": { label: "Card & Board Games", color: "#7B2D26", hasIcon: true },
  "card-&-board-games": { label: "Card & Board Games", color: "#7B2D26", hasIcon: true },
};

// Cache for custom platforms
let platformCache = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 60000; // 1 minute
// Shared in-flight promise so N mounted PlatformBadges dedupe to 1 request.
// Without this, mounting 10 badges simultaneously fires 10 `/platforms` calls
// because they all check the null cache before any of them can populate it.
let platformFetchPromise = null;

// Cache for systems
let systemsCache = null;
let systemsCacheTimestamp = 0;
let systemsFetchPromise = null;

const API = `${getApiUrl()}/api`;

// Common short slugs used in games that need to map to full system names
const SYSTEM_DISPLAY_NAMES = {
  // Xbox
  'xbox-series': 'Xbox Series X|S',
  'xbox-series-xs': 'Xbox Series X|S',
  'xbox-one': 'Xbox One',
  'xbox-360': 'Xbox 360',
  'xbox-pc': 'Windows 10',
  'windows-10': 'Windows 10',
  'windows-8': 'Windows 8',
  // PlayStation  
  'ps5': 'PS5',
  'ps4': 'PS4',
  'ps3': 'PS3',
  'ps-vita': 'PS Vita',
  'psvita': 'PS Vita',
  'psp': 'PSP',
  // Steam/PC
  'steam-windows': 'Windows',
  'steam-mac': 'macOS',
  'steam-linux': 'Linux',
  'steam-pc': 'PC',
  'pc': 'PC',
  // Epic
  'epic-pc': 'PC',
  'epic-mac': 'macOS',
};

// Helper to get system display name from slug
export const getSystemDisplayName = (slug) => {
  if (!slug) return '';

  const lowerSlug = slug.toLowerCase().trim();
  // Normalize whitespace to hyphens so 'windows 8' and 'windows-8' both hit
  // the same key in SYSTEM_DISPLAY_NAMES.
  const normalizedSlug = lowerSlug.replace(/\s+/g, '-');

  // Check direct mapping first (both original + normalized forms)
  if (SYSTEM_DISPLAY_NAMES[lowerSlug]) {
    return SYSTEM_DISPLAY_NAMES[lowerSlug];
  }
  if (SYSTEM_DISPLAY_NAMES[normalizedSlug]) {
    return SYSTEM_DISPLAY_NAMES[normalizedSlug];
  }
  
  // Check systems cache
  if (systemsCache) {
    for (const systems of Object.values(systemsCache)) {
      for (const sys of systems) {
        if (sys.id === slug || sys.id.toLowerCase() === lowerSlug) {
          return sys.name;
        }
        // Also check without platform prefix
        const parts = sys.id.split('-');
        if (parts.length >= 2) {
          const withoutPrefix = parts.slice(1).join('-');
          if (withoutPrefix === lowerSlug || withoutPrefix.toLowerCase() === lowerSlug) {
            return sys.name;
          }
          // Check platform + second part
          if (parts.length >= 3) {
            const platformPlusSecond = parts[0] + '-' + parts[2];
            if (platformPlusSecond === lowerSlug || platformPlusSecond.toLowerCase() === lowerSlug) {
              return sys.name;
            }
          }
        }
      }
    }
  }
  
  // Pattern-based fallbacks — IMPORTANT: check more-specific Windows versions
  // BEFORE the generic "contains windows" fallback, otherwise Windows 8 / 7
  // would be mis-rendered as Windows 10.
  if (lowerSlug.includes('xbox') && lowerSlug.includes('series')) return 'Xbox Series X|S';
  if (lowerSlug.includes('xbox') && lowerSlug.includes('one')) return 'Xbox One';
  if (lowerSlug.includes('xbox') && lowerSlug.includes('360')) return 'Xbox 360';
  if (lowerSlug.includes('windows') && lowerSlug.includes('11')) return 'Windows 11';
  if (lowerSlug.includes('windows') && lowerSlug.includes('10')) return 'Windows 10';
  if (lowerSlug.includes('windows') && lowerSlug.includes('8')) return 'Windows 8';
  if (lowerSlug.includes('windows') && lowerSlug.includes('7')) return 'Windows 7';
  if (lowerSlug.includes('xbox') && lowerSlug.includes('pc')) return 'Windows 10';
  if (lowerSlug.includes('windows')) return 'Windows'; // generic fallback (no version)
  if (lowerSlug === 'ps5' || lowerSlug.includes('ps5')) return 'PS5';
  if (lowerSlug === 'ps4' || lowerSlug.includes('ps4')) return 'PS4';
  if (lowerSlug === 'ps3' || lowerSlug.includes('ps3')) return 'PS3';
  
  // Fallback: format nicely
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper to get an admin-uploaded system logo (iconUrl) from the systems cache.
// Matches across common name/slug variants (e.g. "PS5" == "PlayStation 5").
const _canonKey = (v) => {
  let x = (v || "").toLowerCase().trim();
  x = x.replace(/playstation\s*/g, "ps").replace(/[\s_]+/g, "-").replace(/\|/g, "");
  if (x.includes("xbox") && x.includes("series")) return "xbox-series";
  if (x.includes("xbox") && x.includes("one")) return "xbox-one";
  if (x.includes("xbox") && x.includes("360")) return "xbox-360";
  return x;
};
export const getSystemIconUrl = (slug) => {
  if (!slug || !systemsCache) return null;
  const target = _canonKey(slug);
  const lowerSlug = (slug || "").toLowerCase().trim();
  for (const systems of Object.values(systemsCache)) {
    for (const sys of systems) {
      if (!sys.iconUrl) continue;
      if (sys.id === slug || (sys.id || "").toLowerCase() === lowerSlug) return sys.iconUrl;
      if (_canonKey(sys.name) === target) return sys.iconUrl;
      const parts = (sys.id || "").split("-");
      if (parts.length >= 2 && _canonKey(parts.slice(1).join("-")) === target) return sys.iconUrl;
    }
  }
  return null;
};

// Helper to determine if a color is light or dark
function isLightColor(hexColor) {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
}

export default function PlatformBadge({ platform, size = "md", showLabel = true }) {
  // Normalize platform key for lookups (DB may store "Xbox", config uses "xbox")
  // Also handle known aliases like "Epic Games" → "epic"
  const PLATFORM_ALIASES = {
    "epic games": "epic",
    "card & board": "card-&-board-games",
    "card & board games": "card-&-board-games",
    "card-&-board": "card-&-board-games",
    "google play": "google-play",
  };
  const rawKey = platform?.toLowerCase() || platform;
  const platformKey = PLATFORM_ALIASES[rawKey] || rawKey;
  // Format slug nicely as fallback (e.g., "google-play" → "Google Play")
  const fallbackLabel = platform ? platform.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : platform;
  const [platformConfig, setPlatformConfig] = useState(DEFAULT_PLATFORM_CONFIG[platformKey] || { label: fallbackLabel, color: "#A1A1AA" });
  const [iconUrl, setIconUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (DEFAULT_PLATFORM_CONFIG[platformKey]) {
      setPlatformConfig(DEFAULT_PLATFORM_CONFIG[platformKey]);
    }

    // Always consult the platform list (cached) to pick up the admin-managed logo
    // (iconUrl) — and the color/label for custom platforms.
    const resolve = async () => {
      try {
        const now = Date.now();
        let data = platformCache;
        if (!data || now - cacheTimestamp >= CACHE_DURATION) {
          if (!platformFetchPromise) {
            platformFetchPromise = axios.get(`${API}/platforms`)
              .then((res) => { platformCache = res.data; cacheTimestamp = Date.now(); return res.data; })
              .finally(() => { platformFetchPromise = null; });
          }
          data = await platformFetchPromise;
        }
        if (cancelled || !data) return;
        const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, "-");
        const match = data.find(
          (p) => norm(p.id) === platformKey || norm(p.name) === platformKey || p.id === platform || p.name === platform
        );
        if (match) {
          if (!DEFAULT_PLATFORM_CONFIG[platformKey]) {
            setPlatformConfig({ label: match.name, color: match.color });
          }
          setIconUrl(match.iconUrl || null);
        }
      } catch (error) {
        // non-blocking
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [platform, platformKey]);

  const sizeClasses = {
    xs: "px-1 py-0.5 text-[9px]",
    sm: "px-1.5 py-0.5 text-[10px]",
    md: "px-2 py-1 text-xs",
    lg: "px-3 py-1.5 text-sm",
  };
  
  const iconSizes = {
    xs: "w-2.5 h-2.5",
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  };

  // Determine text color based on background brightness
  const textColor = isLightColor(platformConfig.color) ? "#000000" : "#FFFFFF";

  // Admin-uploaded logo takes priority; otherwise a generic icon is shown.
  const iconNode = iconUrl
    ? <img src={iconUrl} alt={platformConfig.label} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
    : <Gamepad2 className="w-full h-full" strokeWidth={2.5} />;

  // If showing only icon (no label)
  if (!showLabel) {
    return (
      <span
        data-testid={`platform-badge-${platform}`}
        className={`${iconSizes[size]} inline-flex items-center justify-center rounded-md overflow-hidden`}
        style={{
          backgroundColor: platformConfig.color,
          color: textColor,
        }}
        title={platformConfig.label}
      >
        <span className={`${iconSizes[size]} inline-flex items-center justify-center`}>{iconNode}</span>
      </span>
    );
  }

  return (
    <span
      data-testid={`platform-badge-${platform}`}
      className={`${sizeClasses[size]} rounded-md font-semibold inline-flex items-center gap-1`}
      style={{
        backgroundColor: platformConfig.color,
        color: textColor,
        textShadow: isLightColor(platformConfig.color) ? 'none' : '0 1px 2px rgba(0,0,0,0.3)',
      }}
    >
      <span className={`${iconSizes[size]} inline-flex items-center justify-center shrink-0`}>{iconNode}</span>
      {platformConfig.label}
    </span>
  );
}

// System badge with platform-colored styling for better readability
export function SystemBadge({ system, platform, size = "sm" }) {
  const [displayName, setDisplayName] = useState(getSystemDisplayName(system));
  const [iconUrl, setIconUrl] = useState(getSystemIconUrl(system));
  
  useEffect(() => {
    // Fetch systems if cache is stale
    const fetchSystems = async () => {
      const now = Date.now();
      if (systemsCache && now - systemsCacheTimestamp < CACHE_DURATION) {
        setDisplayName(getSystemDisplayName(system));
        setIconUrl(getSystemIconUrl(system));
        return;
      }

      try {
        if (!systemsFetchPromise) {
          systemsFetchPromise = axios.get(`${API}/systems`)
            .then((res) => {
              systemsCache = res.data;
              systemsCacheTimestamp = Date.now();
              return res.data;
            })
            .finally(() => { systemsFetchPromise = null; });
        }
        await systemsFetchPromise;
        setDisplayName(getSystemDisplayName(system));
        setIconUrl(getSystemIconUrl(system));
      } catch (error) {
        console.error("Failed to fetch systems:", error);
        setDisplayName(getSystemDisplayName(system));
      }
    };

    fetchSystems();
  }, [system]);
  
  const sizeClasses = {
    sm: "px-1.5 py-0.5 text-[10px]",
    md: "px-2 py-1 text-xs",
    lg: "px-3 py-1.5 text-sm",
  };

  // Platform color mapping
  const platformColors = {
    xbox: "#107C10",      // Xbox green
    playstation: "#003791", // PlayStation blue
    steam: "#1B2838",     // Steam dark blue
    epic: "#2F2F2F",      // Epic dark gray
    nintendo: "#E60012",  // Nintendo red
    pc: "#5865F2",        // PC purple
    other: "#6B7280",     // Default gray
  };

  // Get color based on platform or system name
  let bgColor = "#6B7280"; // default gray
  
  if (platform) {
    bgColor = platformColors[platform] || "#6B7280";
  } else if (system) {
    // Infer platform from system name
    const systemLower = system.toLowerCase();
    if (systemLower.includes("xbox") || systemLower.includes("xb")) {
      bgColor = platformColors.xbox;
    } else if (systemLower.includes("playstation") || systemLower.includes("ps")) {
      bgColor = platformColors.playstation;
    } else if (systemLower.includes("steam")) {
      bgColor = platformColors.steam;
    } else if (systemLower.includes("switch") || systemLower.includes("nintendo") || systemLower.includes("wii")) {
      bgColor = platformColors.nintendo;
    } else if (systemLower.includes("pc") || systemLower.includes("windows")) {
      bgColor = platformColors.pc;
    }
  }

  const textColor = isLightColor(bgColor) ? "#000000" : "#FFFFFF";

  return (
    <span
      className={`${sizeClasses[size]} rounded-md font-medium inline-flex items-center gap-1`}
      style={{
        backgroundColor: bgColor,
        color: textColor,
        textShadow: isLightColor(bgColor) ? 'none' : '0 1px 2px rgba(0,0,0,0.3)',
      }}
    >
      {iconUrl && (
        <img
          src={iconUrl}
          alt=""
          className="h-3 w-3 object-contain shrink-0"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      {displayName}
    </span>
  );
}

// Export function to clear cache (useful after adding new platforms)
export function clearPlatformCache() {
  platformCache = null;
  cacheTimestamp = 0;
}
